import {
  DownloadOutlined,
  FilterOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Row,
  Card,
  Col,
  Button,
  Input,
  Table,
  Skeleton,
  Tooltip,
  Select,
  Tag,
  Popover,
  Space,
  Typography,
} from "antd";
import React, { useContext, useEffect, useState } from "react";
import { useClient } from "../../Context/ClientContext";
import { useTheme } from "../../Context/ThemeContext";
import { notificationContext } from "../../Common/Routing/PageRoute";
import { ColumnsType } from "antd/es/table";
import CostCentersServices, {
  ICostCentre,
  ICostCentresList,
} from "../../Services/CostCentersServices";
import { ConfirmationModal } from "../../Common/ConfirmationModal";
import InvoiceExportService from "../../Services/InvoiceExportDetails";
import AddCostCentreModel from "./AddCostCentreModel";
import { LedgerStatus } from "../../Services/LedgerService";
import CostCentreTable from "./CostCenterTable";

const { Text } = Typography;
const CostCenters = () => {
  const { themeData } = useTheme();
  const [openCostCentreModel, setOpenCostCentreModel] = useState<{
    open: boolean;
    id: string;
  }>({
    open: false,
    id: "",
  });
  const [searchText, setSearchText] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(
    themeData?.tablePageSize ?? 15
  );
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [refreshTrigger, setRefreshTrigger] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const { currentClient } = useClient();
  const [filter, setfilter] = React.useState<{
    sortCol: string;
    sortDir: string;
  }>({
    sortCol: "",
    sortDir: "",
  });
  const [exportData, setExportData] = React.useState<ICostCentresList[]>([]);
  const [exportType, setExportType] = React.useState<number>(-1);
  const [currentCostCenter, setCurrentCostCenter] = React.useState<{
    id: string | undefined;
    name: string;
    expenseLedgerName: string;
    expenseId?: string;
  }>({
    id: "",
    name: "",
    expenseLedgerName: "",
  });
  const [openPopArray, setOpenPopArray] = useState<boolean[]>([]);
  const [statusFilterChange, setStatusFilterChange] = useState<LedgerStatus>(
    LedgerStatus.active
  );
  const [costCentresList, setCostCentresList] = useState<ICostCentresList[]>(
    []
  );
  const { openNotification } = useContext(notificationContext);
  const [
    changeCostCentreActiveOrDeactive,
    setChangeCostCentreActiveOrDeactive,
  ] = useState<{
    open: boolean;
    id: string;
  }>({
    open: false,
    id: "",
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    open: boolean;
    id: string;
  }>({
    open: false,
    id: "",
  });

  // To delete cost centre
  const handleDelete = async (selId: string) => {
    if (selId) {
      const res = await CostCentersServices.deleteCostcenter(
        currentClient?.id!,
        selId
      );
      if (res.status) {
        openNotification("success", "Cost centre deleted successfully");
        setRefreshTrigger((prev) => !prev);
        setDeleteConfirmation({
          open: false,
          id: "",
        });
      } else {
        openNotification("error", res?.message || "");
        setDeleteConfirmation({
          open: false,
          id: "",
        });
      }
    }
  };

  // Get cost centres list
  const getCostCentresList = async (
    clientId: string,
    start: number,
    length: number,
    search: string,
    sortCol: string,
    sortdir: string,
    expenseId: string,
    status: LedgerStatus
  ) => {
    setDataLoading(true);
    try {
      let res = await CostCentersServices.getCostCentersList(
        clientId,
        start,
        length,
        search,
        sortCol,
        sortdir,
        expenseId,
        status
      );
      {
        console.log(res?.result?.items);
        if (res?.result?.items?.length > 0) {
          const exportWithoutDeleted = res?.result?.items
            ?.filter((x: any) => x?.voucherStatus !== 1)
            ?.map((item: ICostCentresList, index: number) => ({
              ...item,
              srNo: index + 1,
            }));
          setExportData(exportWithoutDeleted);
          setCostCentresList(res?.result?.items);
          setCurrentCostCenter({
            id: res?.result?.items[0]?.id,
            name: res?.result?.items[0]?.costCentreName,
            expenseLedgerName: res?.result?.items[0]?.expenseLedgerName ?? "",
          });
          setTotalRecords(res?.result?.totalRecords);
          setDataLoading(false);
        } else {
          setDataLoading(false);
          setCostCentresList([]);
        }
      }
    } catch (ex: any) {
      setDataLoading(false);
    }
  };
  useEffect(() => {
    if (currentClient?.id)
      getCostCentresList(
        currentClient?.id!,
        (page - 1) * pageSize,
        pageSize,
        searchText,
        filter.sortCol,
        filter.sortDir,
        "",
        statusFilterChange
      );
  }, [
    currentClient?.id,
    statusFilterChange,
    filter.sortCol,
    filter.sortDir,
    refreshTrigger,
    searchText,
    page,
    pageSize,
  ]);

  // To active or deactive cost centre
  const makeCostCentreActiveOrDeactive = async (costcentreid: string) => {
    try {
      let res = await CostCentersServices.setActiveDeactive(
        currentClient?.id!,
        costcentreid
      );
      setChangeCostCentreActiveOrDeactive({ open: false, id: "" });
      setRefreshTrigger((prev) => !prev);
      setDeleteConfirmation({ open: false, id: "" });
    } catch (err: any) {
      console.log(err);
      openNotification("err", err?.message);
      setDeleteConfirmation({ open: false, id: "" });
    }
  };

  const costCentresCols: ColumnsType<ICostCentresList> = [
    {
      title: "Cost Centre",
      dataIndex: "costCentreName",
      sorter: true,
      key: "CostCentreName",
      defaultSortOrder: "ascend",
      className: `custom-ant-table-cell-table-column-sort`,
      render: (value: string, record: ICostCentre) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {" "}
          <div
            onClick={() => {
              setCurrentCostCenter({
                id: record?.id,
                name: record?.costCentreName,
                expenseLedgerName: record?.expenseLedgerName ?? "",
              });
            }}
            style={{
              cursor: "pointer",
              color: "black",
            }}
          >
            {value}
          </div>
        </Skeleton>
      ),
    },
    {
      title: "Expense Ledger",
      dataIndex: "expenseLedgerName",
      className: `custom-ant-table-cell-table-column-sort`,
      sorter: true,
      key: "ExpenseLedgerName",
      render: (value: string, record: ICostCentre) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          <Text
            ellipsis={true}
            title={value}
            onClick={() => {
              setCurrentCostCenter({
                id: record?.id,
                name: record?.costCentreName,
                expenseLedgerName: record?.expenseLedgerName ?? "",
              });
            }}
            style={{
              cursor: "pointer",
              color: "black",
              width: 220,
            }}
          >
            {value}
          </Text>
        </Skeleton>
      ),
    },
    {
      title: "Status",
      className: `custom-ant-table-cell-table-column-sort`,

      render: (record: ICostCentresList) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {record.status === 1 ? (
            <Tag color="blue">Active</Tag>
          ) : (
            <Tag color="red">In Active</Tag>
          )}
        </Skeleton>
      ),
    },
    {
      title: "",
      dataIndex: "id",
      width: "5%",
      className: `custom-ant-table-cell-table-column-sort`,

      render: (value: string, record: ICostCentresList, index: number) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          <Row gutter={18}>
            <Popover
              trigger="click"
              placement="bottom"
              arrow={{ pointAtCenter: true }}
              open={openPopArray[index]}
              onOpenChange={(newOpen) => handlePopoverChange(index, newOpen)}
              content={
                record.status === LedgerStatus.active ? (
                  <Row style={{ maxWidth: "100px" }}>
                    <Col lg={24}>
                      <Tooltip title="Edit" placement="top" color="#3D4998">
                        <Button
                          style={{ width: 100 }}
                          onClick={() => {
                            setOpenCostCentreModel({
                              open: true,
                              id: value,
                            });
                            handlePopoverChange(index, false);
                          }}
                        >
                          Edit
                        </Button>
                      </Tooltip>
                    </Col>

                    <Col lg={24} style={{ marginTop: "6px" }}>
                      <Tooltip title="Delete" placement="top" color="red">
                        <Button
                          style={{ width: 100 }}
                          onClick={() => {
                            setDeleteConfirmation({
                              open: true,
                              id: value,
                            });
                            handlePopoverChange(index, false);
                          }}
                        >
                          Delete
                        </Button>
                      </Tooltip>
                    </Col>

                    <Col lg={24} style={{ marginTop: "6px" }}>
                      <Tooltip title="Make Deactive">
                        <Button
                          style={{ width: 100 }}
                          onClick={() => {
                            setChangeCostCentreActiveOrDeactive({
                              open: true,
                              id: value,
                            });
                            handlePopoverChange(index, false);
                          }}
                        >
                          Deactive
                        </Button>
                      </Tooltip>
                    </Col>
                  </Row>
                ) : (
                  <>
                    <Row style={{ maxWidth: "100px" }}>
                      <Col lg={24}>
                        <Tooltip title="Edit" placement="top" color="#3D4998">
                          <Button style={{ width: 100 }} disabled>
                            Edit
                          </Button>
                        </Tooltip>
                      </Col>

                      <Col lg={24} style={{ marginTop: "6px" }}>
                        <Tooltip title="Delete" placement="top" color="red">
                          <Button style={{ width: 100 }} disabled>
                            Delete
                          </Button>
                        </Tooltip>
                      </Col>

                      <Col lg={24} style={{ marginTop: "6px" }}>
                        <Tooltip title="Make Active">
                          <Button
                            style={{ width: 100 }}
                            onClick={() => {
                              setChangeCostCentreActiveOrDeactive({
                                open: true,
                                id: value,
                              });
                              handlePopoverChange(index, false);
                            }}
                          >
                            Active
                          </Button>
                        </Tooltip>
                      </Col>
                    </Row>
                  </>
                )
              }
            >
              <MoreOutlined style={{ fontSize: "16px" }} />
            </Popover>
          </Row>
        </Skeleton>
      ),
    },
  ];

  const generateFooterRow = (columns: string[]) => {
    return columns.map((col) => {
      return "";
    });
  };

  const columnsA = [
    "S.No.",
    "Cost Centre Name",
    "Expense Ledger's Name",
    "Status",
  ];

  const fieldMapping = {
    "S.No.": (item: ICostCentresList, index: number) => item?.sno,
    "Cost Centre Name": (item: ICostCentresList) => item?.costCentreName,
    "Expense Ledger's Name": (item: ICostCentresList) =>
      item?.expenseLedgerName,
    Status: (item: ICostCentresList) => LedgerStatus[item?.status as number],
  };

  let path = "Cost Center List";
  const exportCsv = (exportData: ICostCentresList[]) => {
    InvoiceExportService.generateCSV(
      exportData,
      columnsA,
      fieldMapping,
      generateFooterRow,
      path,
      currentClient?.businessName
    );
    setExportType(0);
  };

  const exportPDF = (exportData: ICostCentresList[]) => {
    InvoiceExportService.AutoDownloadPDF(
      exportData,
      currentClient?.businessName,
      columnsA,
      fieldMapping,
      generateFooterRow,
      path
    );
    setExportType(0);
  };

  const exportExcel = async (exportData: ICostCentresList[]) => {
    await InvoiceExportService.generateExcel(
      exportData,
      currentClient?.businessName,
      columnsA,
      fieldMapping,
      generateFooterRow,
      path
    );
    setExportType(0);
  };

  // Export options
  const content = (
    <Space direction="vertical">
      <div>
        <Button
          style={{ width: "80px" }}
          size="small"
          title="CSV"
          onClick={() => {
            if (exportData.length > 0) {
              exportCsv(exportData);
            } else {
              setExportType(1);
              // setApiCallExports(true);
            }
          }}
          icon={<DownloadOutlined />}
        >
          CSV
        </Button>
      </div>

      <div>
        <Button
          style={{ width: "80px" }}
          size="small"
          title="PDF"
          onClick={() => {
            if (exportData.length > 0) {
              exportPDF(exportData);
            } else {
              setExportType(2);
              // setApiCallExports(true);
            }
          }}
          icon={<DownloadOutlined />}
        >
          PDF
        </Button>
      </div>
      <div>
        <Button
          style={{ width: "80px" }}
          size="small"
          title="XLSX"
          onClick={() => {
            if (exportData.length > 0) {
              exportExcel(exportData);
            } else {
              setExportType(3);
              // setApiCallExports(true);
            }
          }}
          icon={<DownloadOutlined />}
        >
          XLSX
        </Button>
      </div>
    </Space>
  );
  const tableSort = (pagination?: any, sort?: any, filter?: any) => {
    setfilter({
      sortCol:
        filter.column !== undefined ? filter.columnKey : "CostCentreName",
      sortDir:
        filter.column !== undefined
          ? filter.order === "descend"
            ? "desc"
            : "asc"
          : "desc",
    });
  };
  const handlePopoverChange = (columnIndex: number, newOpen: boolean) => {
    const updatedOpenPopArray = [...openPopArray];
    updatedOpenPopArray[columnIndex] = newOpen;
    setOpenPopArray(updatedOpenPopArray);
  };

  console.log("openPopArray", openPopArray);

  console.log("currentCostCenter: ", currentCostCenter);

  return (
    <>
      <div>
        <div
          style={{
            marginTop: 7,
            padding: "10px 0px 0px 0px",
          }}
        ></div>
        <Row style={{ marginTop: "0px" }} wrap>
          <Col
            xl={8}
            sm={24}
            style={{ paddingRight: "5px", paddingTop: "7px" }}
          >
            <Card
              className="ca-card-body"
              style={{ boxShadow: "0px 0px 10px 0px #96A1C84D" }}
            >
              <div>
                <Row
                  align="middle"
                  style={{
                    borderBottom: "1px solid #DFE9F7",
                    padding: "5px",
                  }}
                  gutter={[{ xxl: 14 }, { md: 5 }]}
                  wrap
                >
                  <Col xxl={12} md={24}>
                    <Input
                      placeholder="Search.."
                      allowClear
                      onClear={() => setSearchText("")}
                      onChange={(e: any) => {
                        // setBankLoading(true);
                        if (!e.cancelable) {
                          const searchBank = setTimeout(() => {
                            setPage(1);
                            setSearchText(e.target.value);
                          }, 1000);
                          return () => clearTimeout(searchBank);
                        } else {
                          setSearchText(e.target.value);
                        }
                      }}
                      suffix={<SearchOutlined />}
                    />
                  </Col>

                  <Col xxl={12} md={24}>
                    <Row justify={"space-between"}>
                      <Col>
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={() =>
                            setOpenCostCentreModel({
                              open: true,
                              id: "",
                            })
                          }
                        >
                          Cost Centre
                        </Button>
                      </Col>
                      <Col>
                        <Row style={{ gap: "10px" }}>
                          <Popover
                            content={
                              <Space direction="vertical">
                                <div>
                                  <Select
                                    defaultValue={LedgerStatus.active}
                                    onChange={(value: LedgerStatus) => {
                                      setStatusFilterChange(value);
                                      handlePopoverChange(-1, false);
                                    }}
                                    style={{ width: "100px" }}
                                    options={[
                                      { value: LedgerStatus.all, label: "All" },
                                      {
                                        value: LedgerStatus.active,
                                        label: "Active",
                                      },
                                      {
                                        value: LedgerStatus.deactive,
                                        label: "Deactive",
                                      },
                                    ]}
                                  />
                                </div>
                              </Space>
                            }
                            placement="bottomLeft"
                            trigger="click"
                            arrow={{ pointAtCenter: true }}
                            open={openPopArray[-1]}
                            onOpenChange={(newOpen) =>
                              handlePopoverChange(-1, newOpen)
                            }
                          >
                            <Button
                              icon={<FilterOutlined />}
                              type="primary"
                            ></Button>
                          </Popover>
                          {costCentresList.length === 0 ? (
                            <Button
                              icon={<DownloadOutlined />}
                              disabled
                              type="primary"
                            ></Button>
                          ) : (
                            <Popover
                              content={content}
                              placement="rightTop"
                              trigger="hover"
                            >
                              <Button
                                icon={<DownloadOutlined />}
                                type="primary"
                              ></Button>
                            </Popover>
                          )}
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </div>
              <div
                className={"withoutanimation"}
                style={{
                  maxHeight: "100%",
                  overflowY: "auto",
                  position: "relative",
                }}
              >
                <Table
                  rowClassName={(record, index) => {
                    return record.id === currentCostCenter?.id
                      ? "custom-ca-active-table"
                      : "";
                  }}
                  className={`Tabel-style-none table-${
                    themeData?.componentSize ?? "middle"
                  } `}
                  locale={{
                    triggerDesc: "",
                    triggerAsc: "",
                    cancelSort: "",
                  }}
                  // rowSelection={rowSelection}
                  columns={costCentresCols}
                  dataSource={costCentresList}
                  onChange={tableSort}
                  pagination={{
                    total: totalRecords,
                    current: page,
                    pageSize: pageSize,
                    showSizeChanger: true,
                    onChange: (page: number, pageSize: number) => {
                      setPage(page);
                      setPageSize(pageSize);
                    },
                    pageSizeOptions: ["10", "15", "25", "50", "100"],
                    showTotal: (totalRecords, page) =>
                      `${page[0]}-${page[1]} of ${totalRecords} items`,
                  }}
                  style={{
                    padding: "5px",
                    overflowY: "auto",
                    overflowX: "auto",
                    maxHeight: "100%",
                  }}
                />
              </div>
            </Card>
          </Col>
          <Col
            xl={16}
            md={24}
            style={{ paddingLeft: "5px", paddingTop: "7px" }}
            className="ca-card-body-padding"
          >
            <Card
              style={{
                border: "1.5px solid #eef2fe",
                marginBottom: "10px",
              }}
            >
              <Row>
                <Col span={8}>
                  {currentCostCenter?.name?.length > 30 ? (
                    <Tooltip
                      title={currentCostCenter?.name}
                      placement="top"
                      color="#3D4998"
                    >
                      {"Name : " +
                        currentCostCenter?.name?.slice(0, 30) +
                        "..."}
                    </Tooltip>
                  ) : (
                    <>
                      <span
                        style={{
                          color: "rgb(139, 143, 165)",
                        }}
                      >
                        Name:{" "}
                      </span>{" "}
                      {currentCostCenter?.name}
                    </>
                  )}
                </Col>
                <Col span={16}>
                  <div>
                    <span
                      style={{
                        paddingRight: "10px",
                        color: "rgb(139, 143, 165)",
                      }}
                    >
                      Expense Ledger Name:
                    </span>
                    <span>{currentCostCenter?.expenseLedgerName}</span>
                  </div>
                </Col>
              </Row>
            </Card>
            <Card
              className="ca-card-body"
              style={{ boxShadow: "0px 0px 10px 0px #96A1C84D" }}
            >
              <div
                style={{
                  height: `calc(100vh - 216px)`,
                  // backgroundColor: "azure",
                  overflowY: "auto",
                }}
              >
                {currentCostCenter.id && (
                  <CostCentreTable
                    costCentreId={currentCostCenter?.id}
                    expenseId={currentCostCenter?.expenseId ?? ""}
                    expenseLedgerName={currentCostCenter?.expenseLedgerName}
                  />
                )}
              </div>
            </Card>
          </Col>
        </Row>
      </div>
      {openCostCentreModel.open && (
        <AddCostCentreModel
          isModalOpen={openCostCentreModel.open}
          // setPostCostCentreData={setPostCostCentreData}
          setIsModalOpen={setOpenCostCentreModel}
          id={openCostCentreModel.id}
          setRefresh={setRefreshTrigger}
        />
      )}
      {deleteConfirmation.open && (
        <ConfirmationModal
          open={deleteConfirmation.open}
          onNo={() =>
            setDeleteConfirmation({
              open: false,
              id: "",
            })
          }
          onYes={() => handleDelete(deleteConfirmation?.id)}
          text={`Are you sure you want to delete this row?`}
        />
      )}
      {changeCostCentreActiveOrDeactive.open && (
        <ConfirmationModal
          open={changeCostCentreActiveOrDeactive.open}
          onNo={() => {
            setChangeCostCentreActiveOrDeactive({
              open: false,
              id: "",
            });
          }}
          onYes={() =>
            makeCostCentreActiveOrDeactive(changeCostCentreActiveOrDeactive.id)
          }
          text={`Are you sure you want to make active or deactive this row?`}
        />
      )}
    </>
  );
};
export default CostCenters;
